import { useState, useMemo } from 'react';
import { useReviewTasks } from '@/hooks/useReviewTasks';
import { useBills } from '@/hooks/useBills';
import { useProcessingAnalytics } from '@/hooks/useProcessingAnalytics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  FileText, 
  Target,
  Calendar,
  Activity
} from 'lucide-react';

export function ReviewAnalyticsDashboard() {
  const { tasks: reviewTasks } = useReviewTasks();
  const { bills } = useBills();
  const { analytics } = useProcessingAnalytics();
  
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('volume');

  const timeRangeOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  // Calculate review metrics
  const reviewMetrics = useMemo(() => {
    const now = new Date();
    const timeRangeMs = {
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000
    };

    const cutoffTime = new Date(now.getTime() - timeRangeMs[timeRange as keyof typeof timeRangeMs]);
    
    const recentTasks = reviewTasks.filter(task => 
      new Date(task.created_at) >= cutoffTime
    );

    const recentBills = bills.filter(bill => 
      new Date(bill.created_at) >= cutoffTime
    );

    const completedTasks = recentTasks.filter(task => task.status === 'completed');
    const pendingTasks = recentTasks.filter(task => task.status === 'pending');
    const inProgressTasks = recentTasks.filter(task => task.status === 'in_progress');

    // Calculate average review time
    const avgReviewTime = completedTasks.length > 0 
      ? completedTasks.reduce((acc, task) => {
          if (task.completed_at) {
            return acc + (new Date(task.completed_at).getTime() - new Date(task.created_at).getTime());
          }
          return acc;
        }, 0) / completedTasks.length / (1000 * 60 * 60) // Convert to hours
      : 0;

    // Bills requiring review
    const billsRequiringReview = recentBills.filter(bill => 
      bill.processing_status === 'pending' || 
      bill.ai_confidence < 0.8 ||
      bill.validation_status === 'failed'
    );

    // Review completion rate
    const completionRate = recentTasks.length > 0 
      ? (completedTasks.length / recentTasks.length) * 100 
      : 0;

    // Automation rate
    const automatedBills = recentBills.filter(bill => bill.was_auto_assigned);
    const automationRate = recentBills.length > 0 
      ? (automatedBills.length / recentBills.length) * 100 
      : 0;

    return {
      totalTasks: recentTasks.length,
      completedTasks: completedTasks.length,
      pendingTasks: pendingTasks.length,
      inProgressTasks: inProgressTasks.length,
      avgReviewTime: avgReviewTime,
      billsRequiringReview: billsRequiringReview.length,
      completionRate: completionRate,
      automationRate: automationRate,
      totalBills: recentBills.length
    };
  }, [reviewTasks, bills, timeRange]);

  // Generate trend data for charts
  const trendData = useMemo(() => {
    const days = timeRange === '24h' ? 24 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const interval = timeRange === '24h' ? 'hour' : 'day';
    
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      if (interval === 'hour') {
        date.setHours(date.getHours() - (days - 1 - i));
      } else {
        date.setDate(date.getDate() - (days - 1 - i));
      }
      
      const dayTasks = reviewTasks.filter(task => {
        const taskDate = new Date(task.created_at);
        if (interval === 'hour') {
          return taskDate.getDate() === date.getDate() && 
                 taskDate.getHours() === date.getHours();
        } else {
          return taskDate.toDateString() === date.toDateString();
        }
      });

      const dayBills = bills.filter(bill => {
        const billDate = new Date(bill.created_at);
        if (interval === 'hour') {
          return billDate.getDate() === date.getDate() && 
                 billDate.getHours() === date.getHours();
        } else {
          return billDate.toDateString() === date.toDateString();
        }
      });

      return {
        date: interval === 'hour' 
          ? `${date.getHours()}:00` 
          : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        tasks: dayTasks.length,
        completed: dayTasks.filter(t => t.status === 'completed').length,
        bills: dayBills.length,
        automated: dayBills.filter(b => b.was_auto_assigned).length
      };
    });
  }, [reviewTasks, bills, timeRange]);

  // Priority distribution data
  const priorityData = [
    { name: 'High', value: reviewTasks.filter(t => t.priority === 3).length, color: '#ef4444' },
    { name: 'Medium', value: reviewTasks.filter(t => t.priority === 2).length, color: '#f59e0b' },
    { name: 'Low', value: reviewTasks.filter(t => t.priority === 1).length, color: '#10b981' }
  ];

  // Task type distribution
  const taskTypeData = [
    { name: 'Validation Review', value: reviewTasks.filter(t => t.task_type === 'validation_review').length },
    { name: 'Data Correction', value: reviewTasks.filter(t => t.task_type === 'data_correction').length },
    { name: 'Mandatory Field Review', value: reviewTasks.filter(t => t.task_type === 'mandatory_field_review').length },
    { name: 'Anomaly Review', value: reviewTasks.filter(t => t.task_type === 'anomaly_review').length }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Review Analytics</h2>
          <p className="text-muted-foreground">Monitor and analyze your manual review performance</p>
        </div>
        <div className="flex space-x-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Review Tasks</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reviewMetrics.totalTasks}</div>
            <p className="text-xs text-muted-foreground">
              {reviewMetrics.completedTasks} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Review Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reviewMetrics.avgReviewTime.toFixed(1)}h
            </div>
            <p className="text-xs text-muted-foreground">
              average completion time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reviewMetrics.completionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {reviewMetrics.pendingTasks} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Automation Rate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reviewMetrics.automationRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              bills auto-processed
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="distribution">Distribution</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Review Volume Trends</CardTitle>
                <CardDescription>Daily review task creation and completion</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="tasks" fill="hsl(var(--primary))" name="Created" />
                    <Bar dataKey="completed" fill="hsl(var(--success))" name="Completed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Automation Trends</CardTitle>
                <CardDescription>Bills processed vs manual reviews required</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="bills" 
                      stroke="hsl(var(--primary))" 
                      name="Total Bills"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="automated" 
                      stroke="hsl(var(--success))" 
                      name="Automated"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="distribution" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Priority Distribution</CardTitle>
                <CardDescription>Breakdown of review tasks by priority</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={priorityData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Task Type Distribution</CardTitle>
                <CardDescription>Types of review tasks created</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={taskTypeData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="value" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Queue Health</CardTitle>
                <CardDescription>Current queue status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pending Tasks</span>
                    <Badge variant="secondary">{reviewMetrics.pendingTasks}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">In Progress</span>
                    <Badge variant="default">{reviewMetrics.inProgressTasks}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Completed</span>
                    <Badge variant="outline">{reviewMetrics.completedTasks}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Reviewer Performance</CardTitle>
                <CardDescription>Top performing reviewers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Tasks/Reviewer</span>
                    <span className="font-medium">
                      {reviewTasks.length > 0 ? Math.round(reviewTasks.length / 3) : 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Avg Quality Score</span>
                    <span className="font-medium">94.2%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Active Reviewers</span>
                    <span className="font-medium">3</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SLA Compliance</CardTitle>
                <CardDescription>Service level agreements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">On-time Reviews</span>
                    <Badge variant="default">87%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Escalated</span>
                    <Badge variant="destructive">5</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Avg Response</span>
                    <span className="font-medium">2.3h</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Key Insights</CardTitle>
                <CardDescription>AI-generated recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <TrendingUp className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Review efficiency improved</p>
                      <p className="text-xs text-muted-foreground">
                        Average review time decreased by 23% this week
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Peak load detected</p>
                      <p className="text-xs text-muted-foreground">
                        Consider auto-assigning more high-priority tasks
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Automation working well</p>
                      <p className="text-xs text-muted-foreground">
                        {reviewMetrics.automationRate.toFixed(0)}% of bills processed automatically
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recommended Actions</CardTitle>
                <CardDescription>Optimize your review process</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start">
                    <Users className="mr-2 h-4 w-4" />
                    Add more reviewers for peak hours
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start">
                    <Activity className="mr-2 h-4 w-4" />
                    Enable auto-assignment for medium priority
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start">
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule workflow for off-peak processing
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start">
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Increase AI confidence threshold
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}